import {
	AccessTime as AccessTimeIcon,
	Apartment as ApartmentIcon,
	CalendarToday as CalendarTodayIcon,
	RestartAlt as RestartAltIcon,
	Save as SaveIcon,
	Sync as SyncIcon
} from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
	AlertProps,
	Box,
	Button,
	FormControl,
	FormControlLabel,
	Grid,
	InputAdornment,
	InputLabel,
	Radio,
	RadioGroup,
	Skeleton,
	TextField,
	Typography
} from "@mui/material";
import moment, { Moment } from "moment";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import ConfirmationDialog from "../../../common/ConfirmationDialog";
import NumberInput from "../../../common/NumberInput";
import { useAppDispatch, useAppSelector } from "../../../redux";
import {
	useGetDeviceTimeQuery,
	useGetNTPStatusQuery,
	useResetNTPMutation,
	useSetNTPServerMutation,
	useSyncDeviceTimeMutation,
	useUpdateBarrierSettingsMutation
} from "../../../redux/reducers/configuration.reducer";
import { showFeedbackNotification } from "../../../redux/reducers/notifications.reducer";
import { useGetSiteConfigQuery } from "../../../redux/reducers/settings.reducer";
import { RESTRICTED_ACCESS_E, getRestrictedAccessList } from "../../../utils/commonUtils";
import { CONFIG_MODE_E, RPI_DEVICE_NAME } from "../../../utils/constants";

enum RELAY_SETTING_E {
	UNAUTHORIZED_LIGHT = 0,
	BARRIER_DOWN = 1
}

interface ISystemSettingsInputs {
	barrier_setting: number;
	relay_setting: RELAY_SETTING_E;
	light_activation_time: number;
	barrier_hold_time: number;
	time: Moment;
	date: Moment;
	ntp_ip: string;
}

const SystemSettings: FC = () => {
	const navigate = useNavigate();
	const dispatch = useAppDispatch();

	const authState = useAppSelector((state) => state.auth);

	const ntpInputRef = useRef<HTMLInputElement>(null);
	const [showResetNTPConfirmation, setShowResetNTPConfirmation] = useState<boolean>(false);

	// GET SITE CONFIG
	const { data: siteConfig, refetch: refetchSiteConfig } = useGetSiteConfigQuery(null, {
		skip: !authState.user.token || authState.user.token === "invalid"
	});

	// GET DEVICE TIME
	const { data: deviceTime, refetch: refetchDeviceTime } = useGetDeviceTimeQuery();

	// SYNC DEVICE TIME
	const [syncDeviceTime, { isLoading: syncTimeLoading, reset: resetSyncDeviceTimeState }] = useSyncDeviceTimeMutation();

	// UPDATE BARRIER SETTINGS
	const [updateBarrierSettings, { isLoading: updateBarrierSettingsLoading, reset: resetUpdateBarrierSettingsState }] =
		useUpdateBarrierSettingsMutation();

	// SET NTP SERVER
	const [setNTPServer, { isLoading: setNTPServerLoading, reset: resetSetNTPServerState }] = useSetNTPServerMutation();

	// RESET NTP
	const [resetNTP, { isLoading: resetNTPLoading, reset: resetResetNTPState }] = useResetNTPMutation();

	// GET NTP STATUS
	const {
		data: ntpStatus,
		isLoading: getNTPStatusLoading,
		isFetching: getNTPStatusFetching,
		refetch: refetchNTPStatus
	} = useGetNTPStatusQuery();

	const RestrictedAccess = useMemo<RESTRICTED_ACCESS_E[]>(
		() => getRestrictedAccessList(siteConfig?.cloud_sync_enabled ?? true),
		[siteConfig]
	);

	const {
		watch,
		register,
		setValue,
		handleSubmit,
		formState: { errors }
	} = useForm<ISystemSettingsInputs>();

	const barrierSetting = watch("barrier_setting");
	const barrierHoldTime = watch("barrier_hold_time");
	const lightActivationTime = watch("light_activation_time");
	const timeInput = watch("time");
	const dateInput = watch("date");
	const relaySetting = watch("relay_setting");
	const ntpIp = watch("ntp_ip");

	const barrierDetailsUpdated = useMemo<boolean>(() => {
		if (siteConfig) {
			if (siteConfig.barrier_config !== Number(barrierSetting)) return true;
			if (siteConfig.barrier_uptime !== Number(barrierHoldTime)) return true;
			if (siteConfig.light_activation_time !== Number(lightActivationTime)) return true;
			if (siteConfig.device_type === RPI_DEVICE_NAME && siteConfig.ml_settings.relay_setting !== Number(relaySetting))
				return true;
		}

		return false;
	}, [barrierHoldTime, barrierSetting, lightActivationTime, relaySetting, siteConfig]);

	const formattedDateInput = useMemo<string>(
		() => `${moment(dateInput).format("YYYY-MM-DD")}_${moment(timeInput).format("HH:mm")}`,
		[dateInput, timeInput]
	);
	const formattedDeviceTime = useMemo<string>(() => {
		if (deviceTime) {
			return `${moment(deviceTime.current_timestamp).format("YYYY-MM-DD")}_${moment(
				deviceTime.current_timestamp
			).format("HH:mm")}`;
		}

		return "";
	}, [deviceTime]);

	const dateTimeUpdated = useMemo<boolean>(
		() =>
			!!deviceTime &&
			!moment(formattedDeviceTime, "YYYY-MM-DD_HH:mm").isSame(moment(formattedDateInput, "YYYY-MM-DD_HH:mm")),
		[deviceTime, formattedDateInput, formattedDeviceTime]
	);

	const ntpIpUpdated = useMemo<boolean>(
		() => (ntpStatus?.custom_ntp_server || "") !== ntpIp,
		[ntpIp, ntpStatus?.custom_ntp_server]
	);

	function notify(message: string, severity?: AlertProps["severity"]) {
		dispatch(showFeedbackNotification({ message, severity }));
	}

	function handleSyncTimeButtonClick() {
		const now = moment();
		syncDeviceTime({
			date: now.format("YYYY-MM-DD"),
			time: now.format("HH:mm:ss")
		})
			.unwrap()
			.then(() => {
				notify("Device time synced successfully", "success");
				refetchDeviceTime();
			})
			.catch((error) => {
				const message = "data" in error && error.data ? String(error.data) : "Failed to sync time";
				notify(message, "error");
			})
			.finally(() => resetSyncDeviceTimeState());
	}

	function handleUpdateDeviceTime(formData: ISystemSettingsInputs): Promise<void> {
		return new Promise((resolve, reject) => {
			syncDeviceTime({
				date: moment(formData.date).format("YYYY-MM-DD"),
				time: moment(formData.time).format("HH:mm:ss")
			})
				.unwrap()
				.then(() => {
					refetchDeviceTime();
					resolve();
				})
				.catch((error) => {
					const message = "data" in error && error.data ? String(error.data) : "Failed to sync time";
					notify(message, "error");
					reject(message);
				})
				.finally(() => resetSyncDeviceTimeState());
		});
	}

	function handleUpdateBarrierSettings(formData: ISystemSettingsInputs): Promise<void> {
		return new Promise((resolve, reject) => {
			if (!siteConfig) {
				reject("Device configuration not found");
				return;
			}

			updateBarrierSettings({
				barrier_config: Number(formData.barrier_setting),
				barrier_uptime: Number(formData.barrier_hold_time),
				light_activation_time: Number(formData.light_activation_time),
				drop_frame: 0,
				ml_settings: {
					...siteConfig.ml_settings,
					relay_setting: Number(formData.relay_setting)
				}
			})
				.unwrap()
				.then(() => {
					refetchSiteConfig();
					resolve();
				})
				.catch((error) => {
					const message = "data" in error && error.data ? String(error.data) : "Failed to update barrier settings";
					notify(message, "error");
					reject(message);
				})
				.finally(() => resetUpdateBarrierSettingsState());
		});
	}

	function handleUpdateNTPServer(formData: ISystemSettingsInputs): Promise<void> {
		return new Promise((resolve, reject) => {
			setNTPServer(formData.ntp_ip)
				.unwrap()
				.then(() => {
					refetchNTPStatus();
					resolve();
				})
				.catch((error) => {
					const message = "data" in error && error.data ? String(error.data) : "Failed to set NTP";
					notify(message, "error");
					reject(message);
				})
				.finally(() => resetSetNTPServerState());
		});
	}

	function handleSaveButtonClick(formData: ISystemSettingsInputs) {
		const updateSettingPromises: Promise<void>[] = [];

		if (barrierDetailsUpdated) {
			updateSettingPromises.push(handleUpdateBarrierSettings(formData));
		}
		if (dateTimeUpdated) {
			updateSettingPromises.push(handleUpdateDeviceTime(formData));
		}
		if (ntpIpUpdated) {
			updateSettingPromises.push(handleUpdateNTPServer(formData));
		}

		Promise.all(updateSettingPromises)
			.then(() => notify("System settings saved successfully", "success"))
			.catch(() => {});
	}

	function handleEditBuildingConfigButtonClick() {
		navigate("/configuration/system/building-config");
	}

	function handleEditCustomFieldsButtonClick() {
		navigate("/configuration/system/custom-fields");
	}

	function openResetNTPConfirmationDialog() {
		setShowResetNTPConfirmation(true);
	}

	function closeResetNTPConfirmationDialog() {
		setShowResetNTPConfirmation(false);
	}

	function handleConfirmResetNTP() {
		resetNTP()
			.unwrap()
			.then(() => {
				notify("NTP reset successfully", "success");
				resetResetNTPState();
				refetchNTPStatus();
			})
			.catch((error) => {
				notify("data" in error && error.data ? String(error.data) : "Failed to reset NTP", "error");
			})
			.finally(() => {
				closeResetNTPConfirmationDialog();
			});
	}

	useEffect(() => {
		if (siteConfig) {
			setValue("barrier_setting", siteConfig.barrier_config);
			setValue("barrier_hold_time", siteConfig.barrier_uptime);
			setValue("light_activation_time", siteConfig.light_activation_time);
			setValue("relay_setting", siteConfig.ml_settings.relay_setting);
		}
	}, [setValue, siteConfig]);

	useEffect(() => {
		if (deviceTime) {
			setValue("time", moment(deviceTime.current_timestamp));
			setValue("date", moment(deviceTime.current_timestamp));
		}
	}, [deviceTime, setValue]);

	useEffect(() => {
		if (ntpStatus) {
			setValue("ntp_ip", (ntpStatus.custom_ntp_configured && ntpStatus.custom_ntp_server) || "");
		}
	}, [ntpStatus, setValue]);

	return (
		<Box
			className="configuration-screen-wrapper flex-wrapper"
			component="form"
			noValidate
			onSubmit={handleSubmit(handleSaveButtonClick)}
			sx={{
				display: "flex",
				flexDirection: "column",
				gap: { xs: "1rem", sm: "1.5rem" }
			}}
		>
			{!RestrictedAccess.includes(RESTRICTED_ACCESS_E.EDIT_CONFIG_PAGE) ? (
				<Box
					className="system-settings-section-wrapper"
					sx={{
						background: "#ffffff",
						padding: { xs: "1rem", sm: "1.5rem" },
						borderRadius: "12px",
						boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
						border: "1px solid rgba(0, 0, 0, 0.06)"
					}}
				>
					<Typography
						variant="h6"
						color="var(--color-primary-main)"
						sx={{ fontWeight: 600, fontSize: { xs: "1.125rem", sm: "1.25rem" } }}
					>
						Device Type
					</Typography>

					<FormControl fullWidth>
						<RadioGroup
							row
							sx={{
								gap: { xs: "1rem", sm: "2rem" },
								flexDirection: { xs: "column", sm: "row" }
							}}
						>
							<FormControlLabel
								value={0}
								control={<Radio />}
								label="Single (Entry)"
								checked={Number(barrierSetting) === 0}
								{...register("barrier_setting", { required: true })}
								sx={{
									"& .MuiFormControlLabel-label": {
										fontSize: { xs: "0.875rem", sm: "1rem" }
									}
								}}
							/>

							<FormControlLabel
								value={1}
								control={<Radio />}
								label="Single (Exit)"
								checked={Number(barrierSetting) === 1}
								{...register("barrier_setting", { required: true })}
								sx={{
									"& .MuiFormControlLabel-label": {
										fontSize: { xs: "0.875rem", sm: "1rem" }
									}
								}}
							/>

							{siteConfig && siteConfig.device_type !== RPI_DEVICE_NAME ? (
								<FormControlLabel
									value={2}
									control={<Radio />}
									label="Double (Entry + Exit)"
									checked={Number(barrierSetting) === 2}
									{...register("barrier_setting", { required: true })}
									sx={{
										"& .MuiFormControlLabel-label": {
											fontSize: { xs: "0.875rem", sm: "1rem" }
										}
									}}
								/>
							) : null}
						</RadioGroup>
					</FormControl>
				</Box>
			) : null}

			{siteConfig &&
			siteConfig.device_type === RPI_DEVICE_NAME &&
			!RestrictedAccess.includes(RESTRICTED_ACCESS_E.EDIT_CONFIG_PAGE) ? (
				<Box
					className="system-settings-section-wrapper"
					sx={{
						background: "#ffffff",
						padding: { xs: "1rem", sm: "1.5rem" },
						borderRadius: "12px",
						boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
						border: "1px solid rgba(0, 0, 0, 0.06)"
					}}
				>
					<Typography
						variant="h6"
						color="var(--color-primary-main)"
						sx={{ fontWeight: 600, fontSize: { xs: "1.125rem", sm: "1.25rem" } }}
					>
						Relay Settings
					</Typography>

					<Grid container spacing={{ xs: 2, sm: 3 }}>
						<Grid item xs={12} md={6}>
							<Box
								sx={{
									padding: { xs: "1rem", sm: "1.5rem" },
									borderRadius: "12px",
									border: "2px solid",
									cursor: "pointer",
									transition: "all 0.3s ease",
									background:
										Number(relaySetting) === RELAY_SETTING_E.UNAUTHORIZED_LIGHT
											? "rgba(91, 78, 191, 0.03)"
											: "linear-gradient(135deg, #ffffff 0%, #fafbfc 100%)",
									borderColor:
										Number(relaySetting) === RELAY_SETTING_E.UNAUTHORIZED_LIGHT ? "#5b4ebf" : "rgba(0, 0, 0, 0.12)",
									boxShadow:
										Number(relaySetting) === RELAY_SETTING_E.UNAUTHORIZED_LIGHT
											? "0 4px 20px rgba(91, 78, 191, 0.15)"
											: "0 2px 8px rgba(0, 0, 0, 0.06)",
									"&:hover": {
										transform: "translateY(-2px)",
										boxShadow:
											Number(relaySetting) === RELAY_SETTING_E.UNAUTHORIZED_LIGHT
												? "0 6px 25px rgba(91, 78, 191, 0.2)"
												: "0 4px 16px rgba(0, 0, 0, 0.1)"
									}
								}}
								onClick={() => setValue("relay_setting", RELAY_SETTING_E.UNAUTHORIZED_LIGHT)}
							>
								<Box sx={{ display: "flex", alignItems: "center", marginBottom: { xs: "0.75rem", sm: "1rem" } }}>
									<Box
										sx={{
											width: { xs: "18px", sm: "20px" },
											height: { xs: "18px", sm: "20px" },
											borderRadius: "50%",
											border: "2px solid",
											borderColor:
												Number(relaySetting) === RELAY_SETTING_E.UNAUTHORIZED_LIGHT ? "#5b4ebf" : "rgba(0, 0, 0, 0.3)",
											background:
												Number(relaySetting) === RELAY_SETTING_E.UNAUTHORIZED_LIGHT ? "#5b4ebf" : "transparent",
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											marginRight: { xs: "0.5rem", sm: "0.75rem" }
										}}
									>
										{Number(relaySetting) === RELAY_SETTING_E.UNAUTHORIZED_LIGHT && (
											<Box
												sx={{
													width: { xs: "6px", sm: "8px" },
													height: { xs: "6px", sm: "8px" },
													borderRadius: "50%",
													background: "white"
												}}
											/>
										)}
									</Box>
									<Typography
										variant="h6"
										sx={{
											fontWeight: 600,
											fontSize: { xs: "1rem", sm: "1.25rem" },
											color:
												Number(relaySetting) === RELAY_SETTING_E.UNAUTHORIZED_LIGHT ? "#5b4ebf" : "rgba(0, 0, 0, 0.7)"
										}}
									>
										Unregistered Signalling Light
									</Typography>
								</Box>

								<InputLabel
									htmlFor="light-activation-time-input"
									sx={{
										fontWeight: 500,
										marginBottom: { xs: "0.75rem", sm: "1rem" },
										fontSize: { xs: "0.875rem", sm: "1rem" },
										color:
											Number(relaySetting) === RELAY_SETTING_E.UNAUTHORIZED_LIGHT
												? "rgba(0, 0, 0, 0.87)"
												: "rgba(0, 0, 0, 0.38)"
									}}
								>
									Light Activation Time
								</InputLabel>

								<NumberInput
									required
									fullWidth
									positiveOnly
									disableSigned
									disableDecimal
									size="small"
									variant="outlined"
									color="primary"
									id="light-activation-time-input"
									error={!!errors.light_activation_time}
									disabled={Number(relaySetting) !== RELAY_SETTING_E.UNAUTHORIZED_LIGHT}
									helperText={
										errors.light_activation_time && errors.light_activation_time.message
											? errors.light_activation_time.message.toString()
											: ""
									}
									InputProps={{
										endAdornment: <InputAdornment position="end">s</InputAdornment>
									}}
									{...register("light_activation_time", {
										required: "Light activation time is required",
										min: { value: 0, message: "Light activation time cannot be negative" }
									})}
								/>
							</Box>
						</Grid>

						<Grid item xs={12} md={6}>
							<Box
								sx={{
									padding: { xs: "1rem", sm: "1.5rem" },
									borderRadius: "12px",
									border: "2px solid",
									cursor: "pointer",
									transition: "all 0.3s ease",
									background:
										Number(relaySetting) === RELAY_SETTING_E.BARRIER_DOWN
											? "rgba(91, 78, 191, 0.03)"
											: "linear-gradient(135deg, #ffffff 0%, #fafbfc 100%)",
									borderColor:
										Number(relaySetting) === RELAY_SETTING_E.BARRIER_DOWN ? "#5b4ebf" : "rgba(0, 0, 0, 0.12)",
									boxShadow:
										Number(relaySetting) === RELAY_SETTING_E.BARRIER_DOWN
											? "0 4px 20px rgba(91, 78, 191, 0.15)"
											: "0 2px 8px rgba(0, 0, 0, 0.06)",
									"&:hover": {
										transform: "translateY(-2px)",
										boxShadow:
											Number(relaySetting) === RELAY_SETTING_E.BARRIER_DOWN
												? "0 6px 25px rgba(91, 78, 191, 0.2)"
												: "0 4px 16px rgba(0, 0, 0, 0.1)"
									}
								}}
								onClick={() => setValue("relay_setting", RELAY_SETTING_E.BARRIER_DOWN)}
							>
								<Box sx={{ display: "flex", alignItems: "center", marginBottom: { xs: "0.75rem", sm: "1rem" } }}>
									<Box
										sx={{
											width: { xs: "18px", sm: "20px" },
											height: { xs: "18px", sm: "20px" },
											borderRadius: "50%",
											border: "2px solid",
											borderColor:
												Number(relaySetting) === RELAY_SETTING_E.BARRIER_DOWN ? "#5b4ebf" : "rgba(0, 0, 0, 0.3)",
											background: Number(relaySetting) === RELAY_SETTING_E.BARRIER_DOWN ? "#5b4ebf" : "transparent",
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											marginRight: { xs: "0.5rem", sm: "0.75rem" }
										}}
									>
										{Number(relaySetting) === RELAY_SETTING_E.BARRIER_DOWN && (
											<Box
												sx={{
													width: { xs: "6px", sm: "8px" },
													height: { xs: "6px", sm: "8px" },
													borderRadius: "50%",
													background: "white"
												}}
											/>
										)}
									</Box>
									<Typography
										variant="h6"
										sx={{
											fontWeight: 600,
											fontSize: { xs: "1rem", sm: "1.25rem" },
											color: Number(relaySetting) === RELAY_SETTING_E.BARRIER_DOWN ? "#5b4ebf" : "rgba(0, 0, 0, 0.7)"
										}}
									>
										Barrier Down
									</Typography>
								</Box>

								<InputLabel
									htmlFor="barrier-hold-time-input"
									sx={{
										fontWeight: 500,
										marginBottom: { xs: "0.75rem", sm: "1rem" },
										fontSize: { xs: "0.875rem", sm: "1rem" },
										color:
											Number(relaySetting) === RELAY_SETTING_E.BARRIER_DOWN
												? "rgba(0, 0, 0, 0.87)"
												: "rgba(0, 0, 0, 0.38)"
									}}
								>
									Barrier Hold Time
								</InputLabel>

								<NumberInput
									required
									fullWidth
									positiveOnly
									disableSigned
									disableDecimal
									size="small"
									variant="outlined"
									color="primary"
									id="barrier-hold-time-input"
									error={!!errors.barrier_hold_time}
									disabled={Number(relaySetting) !== RELAY_SETTING_E.BARRIER_DOWN}
									helperText={
										errors.barrier_hold_time && errors.barrier_hold_time.message
											? errors.barrier_hold_time.message.toString()
											: ""
									}
									InputProps={{
										endAdornment: <InputAdornment position="end">s</InputAdornment>
									}}
									{...register("barrier_hold_time", {
										required: "Barrier hold time is required",
										min: { value: 0, message: "Barrier hold time cannot be negative" }
									})}
								/>
							</Box>
						</Grid>
					</Grid>

					<Typography
						variant="body2"
						color="var(--color-success-main)"
						sx={{
							fontWeight: 500,
							fontSize: { xs: "0.8125rem", sm: "0.875rem" },
							marginTop: { xs: "0.75rem", sm: "1rem" }
						}}
					>
						*Note: Relay connections are required as per the option selected.
					</Typography>
				</Box>
			) : !RestrictedAccess.includes(RESTRICTED_ACCESS_E.EDIT_CONFIG_PAGE) ? (
				<Box
					className="system-settings-section-wrapper"
					sx={{
						background: "#ffffff",
						padding: { xs: "1rem", sm: "1.5rem" },
						borderRadius: "12px",
						boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
						border: "1px solid rgba(0, 0, 0, 0.06)"
					}}
				>
					<Typography
						variant="h6"
						color="var(--color-primary-main)"
						sx={{
							fontWeight: 600,
							fontSize: { xs: "1.125rem", sm: "1.25rem" },
							marginBottom: { xs: "0.75rem", sm: "1rem" }
						}}
					>
						Barrier Settings
					</Typography>

					<Box className="barrier-inputs-wrapper">
						<Grid container spacing={{ xs: 2, sm: 2 }}>
							<Grid item xs={12} md={4} xl={3}>
								<InputLabel
									htmlFor="barrier-hold-time-input"
									sx={{
										fontWeight: 500,
										marginBottom: "1rem",
										fontSize: { xs: "0.875rem", sm: "1rem" }
									}}
								>
									Barrier Hold Time
								</InputLabel>

								<NumberInput
									required
									fullWidth
									positiveOnly
									disableSigned
									disableDecimal
									size="small"
									variant="outlined"
									color="primary"
									id="barrier-hold-time-input"
									error={!!errors.barrier_hold_time}
									helperText={
										errors.barrier_hold_time && errors.barrier_hold_time.message
											? errors.barrier_hold_time.message.toString()
											: ""
									}
									InputProps={{
										endAdornment: <InputAdornment position="end">s</InputAdornment>
									}}
									{...register("barrier_hold_time", {
										required: "Barrier hold time is required",
										min: { value: 0, message: "Barrier hold time cannot be negative" }
									})}
								/>
							</Grid>

							<Grid item xs={12} md={4} xl={3}>
								<InputLabel
									htmlFor="light-activation-time-input"
									sx={{
										fontWeight: 500,
										marginBottom: "1rem",
										fontSize: { xs: "0.875rem", sm: "1rem" }
									}}
								>
									Light Activation Time
								</InputLabel>

								<NumberInput
									required
									fullWidth
									positiveOnly
									disableSigned
									disableDecimal
									size="small"
									variant="outlined"
									color="primary"
									id="light-activation-time-input"
									error={!!errors.light_activation_time}
									helperText={
										errors.light_activation_time && errors.light_activation_time.message
											? errors.light_activation_time.message.toString()
											: ""
									}
									InputProps={{
										endAdornment: <InputAdornment position="end">s</InputAdornment>
									}}
									{...register("light_activation_time", {
										required: "Light activation time is required",
										min: { value: 0, message: "Light activation time cannot be negative" }
									})}
								/>
							</Grid>
						</Grid>
					</Box>
				</Box>
			) : null}

			{!RestrictedAccess.includes(RESTRICTED_ACCESS_E.EDIT_CONFIG_PAGE) ? (
				<Box
					className="system-settings-section-wrapper"
					sx={{
						background: "#ffffff",
						padding: { xs: "1rem", sm: "1.5rem" },
						borderRadius: "12px",
						boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
						border: "1px solid rgba(0, 0, 0, 0.06)"
					}}
				>
					<Typography
						variant="h6"
						color="var(--color-primary-main)"
						sx={{ fontWeight: 600, fontSize: { xs: "1.125rem", sm: "1.25rem" } }}
					>
						Time and Date Settings
					</Typography>

					<Box className="date-time-settings-inputs-wrapper">
						{getNTPStatusLoading || getNTPStatusFetching ? (
							<Grid container spacing={2} alignItems="flex-end">
								<Grid item xs={12} sm={5} md={4}>
									<Skeleton variant="text" width={120} height={20} sx={{ marginBottom: "0.5rem" }} />
									<Skeleton variant="rectangular" width="100%" height={40} sx={{ borderRadius: "4px" }} />
								</Grid>

								<Grid item xs={12} sm={5} md={4}>
									<Skeleton variant="rectangular" width={120} height={36} sx={{ borderRadius: "4px" }} />
								</Grid>
							</Grid>
						) : (
							<Grid container spacing={2} alignItems="flex-end">
								<Grid item xs={12} sm={5} md={4}>
									<InputLabel
										htmlFor="ntp-ip-input"
										sx={{ fontWeight: 500, marginBottom: "0.5rem", fontSize: { xs: "0.875rem", sm: "1rem" } }}
									>
										NTP Server IP
									</InputLabel>

									<TextField
										fullWidth
										size="small"
										variant="outlined"
										color="primary"
										id="ntp-ip-input"
										error={!!errors.ntp_ip}
										helperText={errors.ntp_ip?.message?.toString() || ""}
										inputRef={ntpInputRef}
										autoComplete="off"
										{...register("ntp_ip")}
									/>
								</Grid>

								<Grid item xs={12} sm={5} md={4}>
									<Box
										sx={{
											display: "flex",
											alignItems: "center",
											height: ntpInputRef.current?.offsetHeight
										}}
									>
										<LoadingButton
											variant="outlined"
											color="primary"
											startIcon={<RestartAltIcon />}
											onClick={openResetNTPConfirmationDialog}
											loading={resetNTPLoading}
											disabled={!ntpStatus?.custom_ntp_configured || false}
										>
											Reset to Default
										</LoadingButton>
									</Box>
								</Grid>
							</Grid>
						)}
					</Box>

					<Box className="date-time-settings-inputs-wrapper">
						<Grid container spacing={{ xs: 2, sm: 2 }} alignItems="center">
							<Grid item xs={12} sm={5} md={4}>
								<Box className="device-time-card">
									<Box className="device-time-card-icon">
										<AccessTimeIcon sx={{ color: "#ffffff", fontSize: { xs: "18px", sm: "20px" } }} />
									</Box>

									<Box sx={{ flex: 1, minWidth: 0 }}>
										<Typography
											variant="caption"
											sx={{
												fontSize: { xs: "0.6875rem", sm: "0.75rem" },
												color: "rgba(0, 0, 0, 0.6)",
												fontWeight: 500,
												textTransform: "uppercase",
												letterSpacing: "0.5px",
												marginBottom: "0.125rem",
												display: "block"
											}}
										>
											Device Time
										</Typography>

										<Typography
											variant="body1"
											sx={{
												fontSize: { xs: "0.9375rem", sm: "1rem" },
												fontWeight: 600,
												color: "rgba(0, 0, 0, 0.87)",
												lineHeight: 1.2,
												wordBreak: "break-word"
											}}
										>
											{timeInput ? moment(timeInput).format("hh:mm A") : "--:-- --"}
										</Typography>
									</Box>
								</Box>
							</Grid>

							<Grid item xs={12} sm={5} md={4}>
								<Box className="device-time-card">
									<Box className="device-time-card-icon">
										<CalendarTodayIcon sx={{ color: "#ffffff", fontSize: { xs: "18px", sm: "20px" } }} />
									</Box>

									<Box sx={{ flex: 1, minWidth: 0 }}>
										<Typography
											variant="caption"
											sx={{
												fontSize: { xs: "0.6875rem", sm: "0.75rem" },
												color: "rgba(0, 0, 0, 0.6)",
												fontWeight: 500,
												textTransform: "uppercase",
												letterSpacing: "0.5px",
												marginBottom: "0.125rem",
												display: "block"
											}}
										>
											Device Date
										</Typography>

										<Typography
											variant="body1"
											sx={{
												fontSize: { xs: "0.9375rem", sm: "1rem" },
												fontWeight: 600,
												color: "rgba(0, 0, 0, 0.87)",
												lineHeight: 1.2,
												wordBreak: "break-word"
											}}
										>
											{dateInput ? moment(dateInput).format("DD/MM/YYYY") : "--/--/----"}
										</Typography>
									</Box>
								</Box>
							</Grid>

							<Grid item xs={12} sm={12} md={4}>
								<Box sx={{ display: "flex", alignItems: "center" }}>
									<LoadingButton
										variant="contained"
										color="primary"
										startIcon={<SyncIcon />}
										loadingPosition="start"
										onClick={handleSyncTimeButtonClick}
										sx={{ width: { xs: "100%", sm: "auto" } }}
									>
										Sync with Browser
									</LoadingButton>
								</Box>
							</Grid>
						</Grid>
					</Box>
				</Box>
			) : null}

			{!RestrictedAccess.includes(RESTRICTED_ACCESS_E.EDIT_BUILDING_CONFIG) &&
			siteConfig?.mode !== CONFIG_MODE_E.MY_GATE ? (
				<Box
					className="system-settings-section-wrapper"
					sx={{
						background: "#ffffff",
						padding: { xs: "1rem", sm: "1.5rem" },
						borderRadius: "12px",
						boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
						border: "1px solid rgba(0, 0, 0, 0.06)"
					}}
				>
					<Typography
						variant="h6"
						color="var(--color-primary-main)"
						sx={{ fontWeight: 600, fontSize: { xs: "1.125rem", sm: "1.25rem" } }}
					>
						Property Configuration
					</Typography>

					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							gap: "0.75rem",
							flexDirection: { xs: "column", sm: "row" },
							width: "100%"
						}}
					>
						<Button
							variant="contained"
							color="primary"
							startIcon={<ApartmentIcon />}
							onClick={handleEditBuildingConfigButtonClick}
							size="large"
							sx={{ width: { xs: "100%", sm: "auto" } }}
						>
							Edit Property Configuration
						</Button>

						<Button
							variant="contained"
							color="primary"
							startIcon={<ApartmentIcon />}
							onClick={handleEditCustomFieldsButtonClick}
							size="large"
							sx={{ width: { xs: "100%", sm: "auto" } }}
						>
							Edit Custom Fields
						</Button>
					</Box>
				</Box>
			) : null}

			{!RestrictedAccess.includes(RESTRICTED_ACCESS_E.EDIT_CONFIG_PAGE) &&
			((siteConfig && barrierDetailsUpdated) || dateTimeUpdated || ntpIpUpdated) ? (
				<Box
					sx={{
						position: "sticky",
						bottom: 0,
						left: 0,
						right: 0,
						display: "flex",
						alignItems: "center",
						justifyContent: "flex-end",
						padding: { xs: "1rem", sm: "2rem 1rem" },
						background: "rgba(255, 255, 255, 0.8)",
						backdropFilter: "blur(20px)",
						WebkitBackdropFilter: "blur(20px)",
						border: "1px solid rgba(255, 255, 255, 0.3)",
						borderRadius: "16px",
						zIndex: 999,
						marginTop: "auto",
						boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
						"&::before": {
							content: '""',
							position: "absolute",
							top: 0,
							left: 0,
							right: 0,
							bottom: 0,
							borderRadius: "16px",
							padding: "1px",
							background: "linear-gradient(135deg, rgba(255, 255, 255, 0.4) 0%, rgba(255, 255, 255, 0.1) 100%)",
							mask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
							maskComposite: "exclude",
							WebkitMask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
							WebkitMaskComposite: "xor"
						}
					}}
				>
					<LoadingButton
						variant="contained"
						color="success"
						startIcon={<SaveIcon />}
						loading={updateBarrierSettingsLoading || syncTimeLoading || setNTPServerLoading}
						loadingPosition="start"
						type="submit"
						sx={{
							fontSize: { xs: "0.875rem", sm: "1rem" },
							padding: { xs: "8px 16px", sm: "8px 16px" },
							width: { xs: "100%", sm: "auto" }
						}}
					>
						Save Changes
					</LoadingButton>
				</Box>
			) : null}

			<ConfirmationDialog
				open={showResetNTPConfirmation}
				title="Reset NTP Configuration"
				heading="Are you sure you want to reset NTP to default?"
				onClose={closeResetNTPConfirmationDialog}
				onConfirm={handleConfirmResetNTP}
				color="primary"
				confirmButtonLoading={resetNTPLoading}
				confirmationButtonText="Reset to Default"
				width="xs"
			/>
		</Box>
	);
};

export default SystemSettings;
